import React, { useState } from "react";
import { User, UserPlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const CUSTOMER_KEY = "adc_customer";
const field = "w-full bg-noir border border-white/15 px-4 py-3 font-body text-sm text-cream placeholder:text-white/30 focus:border-gold focus:outline-none transition-colors";

export const CheckoutAuthDialog = ({ open, onClose, onProceed }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [optIn, setOptIn] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/customers/login" : "/customers/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name, marketing_opt_in: optIn };
      const res = await api.post(path, payload);
      const data = { token: res.data.token, user: res.data.user };
      localStorage.setItem(CUSTOMER_KEY, JSON.stringify(data));
      window.dispatchEvent(new Event("adc-auth-change"));
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      onProceed(data.user.email, optIn);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-noir-2 border border-white/10 text-cream max-w-md" data-testid="checkout-auth-dialog" aria-describedby="checkout-auth-desc">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-cream">Before you checkout</DialogTitle>
        </DialogHeader>
        <p id="checkout-auth-desc" className="font-body text-sm text-white/50 -mt-1">
          Sign in for faster checkout and order history — or continue as a guest.
        </p>

        <div className="flex border border-white/10 mt-4 mb-5">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`checkout-tab-${m}`}
              className={`flex-1 py-2.5 font-body uppercase tracking-[0.15em] text-[11px] transition-colors ${mode === m ? "bg-gold text-black" : "text-white/60 hover:text-cream"}`}>
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="checkout-auth-form">
          {mode === "register" && (
            <input placeholder="Name" value={form.name} data-testid="checkout-auth-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
          )}
          <input type="email" required placeholder="Email" value={form.email} data-testid="checkout-auth-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
          <input type="password" required placeholder="Password" value={form.password} data-testid="checkout-auth-password"
            onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
          <button type="submit" disabled={loading} data-testid="checkout-auth-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors disabled:opacity-50">
            {mode === "login" ? <User className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {loading ? "Please wait…" : mode === "login" ? "Sign In & Continue" : "Create & Continue"}
          </button>
        </form>

        <label className="flex items-start gap-2.5 cursor-pointer mt-4" data-testid="checkout-optin-label">
          <input type="checkbox" checked={optIn} data-testid="checkout-optin"
            onChange={(e) => setOptIn(e.target.checked)} className="accent-[#D4AF37] mt-0.5" />
          <span className="font-body text-xs text-white/55 leading-relaxed">Email me future offers, discounts & new sculpted designs.</span>
        </label>

        <div className="mt-5 text-center border-t border-white/10 pt-5">
          <p className="font-body text-xs text-white/40 mb-3">No account needed to order.</p>
          <button onClick={() => onProceed(null, optIn)} data-testid="checkout-guest-btn"
            className="inline-flex items-center gap-2 font-body uppercase tracking-[0.2em] text-xs text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors">
            Continue as Guest <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutAuthDialog;
